// components/MyPage/MyEmoticons.jsx
// components/MyPage/MyEmoticons.jsx
import { useEffect, useState } from 'react';
import {
    Box, Typography, Card, CardMedia, CardContent,
    Grid, IconButton, Skeleton, Pagination, Chip,
    List, ListItem, ListItemAvatar, ListItemText, Avatar, useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';

const getFullImageUrl = (url) =>
    url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

export default function MyEmoticons() {
    const [emoticons, setEmoticons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [equippedId, setEquippedId] = useState(null);
    const [page, setPage] = useState(1);
    const { refreshUserInfo } = useUser();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const CARD_W = isMobile ? 120 : 140;
    const CARD_H = isMobile ? 150 : 168;
    const MOBILE_PAGE_SIZE = 6;
    const DEFAULT_PAGE_SIZE = 10;
    const PAGE_SIZE = isMobile ? MOBILE_PAGE_SIZE : DEFAULT_PAGE_SIZE;
    const SKELETON_COUNT = PAGE_SIZE;

    const fetchMyEmoticons = () => {
        api.get('/api/rewards/emoticons/my/')
            .then(res => {
                setEmoticons(res.data || []);
                const eq = res.data.find(e => e.is_equipped);
                setEquippedId(eq ? eq.emoticon.id : null);
            })
            .finally(() => setLoading(false));
    };

    const handleToggle = (id) => {
        const targetId = id === equippedId ? 'none' : id;
        api.post(`/api/rewards/emoticons/equip/${targetId}/`)
            .then(() => {
                fetchMyEmoticons();
                refreshUserInfo();
            })
            .catch(err => alert(err.response?.data?.detail || '장착/해제 실패'));
    };

    useEffect(fetchMyEmoticons, []);

    const hoverFX = {
        transition: 'box-shadow .15s ease-in-out',
        '&:hover': { boxShadow: 4 },
    };

    const shouldPaginateMobile = isMobile && emoticons.length > MOBILE_PAGE_SIZE;
    const pageCount = Math.ceil(
        shouldPaginateMobile
            ? emoticons.length / MOBILE_PAGE_SIZE
            : emoticons.length / DEFAULT_PAGE_SIZE
    ) || 1;

    const slice = isMobile
        ? shouldPaginateMobile
            ? emoticons.slice((page - 1) * MOBILE_PAGE_SIZE, page * MOBILE_PAGE_SIZE)
            : emoticons
        : emoticons.slice((page - 1) * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE);

    return (
        <Box p={isMobile ? 1.5 : 2}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                🎭 내 이모티콘
            </Typography>

            {loading ? (
                isMobile ? (
                    Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 2 }} />
                    ))
                ) : (
                    <Grid container spacing={2} justifyContent="flex-start">
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <Grid item key={`sk-${i}`} sx={{ flex: '0 0 auto', width: CARD_W }}>
                                <Card sx={{ width: CARD_W, height: CARD_H, ...hoverFX }}>
                                    <Skeleton variant="rectangular" height={100} />
                                    <CardContent sx={{ pt: 1 }}>
                                        <Skeleton width="80%" height={24} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )
            ) : (
                <>
                    {isMobile ? (
                        <List disablePadding>
                            {slice.map(e => (
                                <ListItem
                                    key={e.id}
                                    divider
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            color={e.is_equipped ? 'primary' : 'default'}
                                            onClick={() => handleToggle(e.emoticon.id)}
                                        >
                                            {e.is_equipped ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                        </IconButton>
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            variant="rounded"
                                            src={e.emoticon.image ? getFullImageUrl(e.emoticon.image) : undefined}
                                            alt={e.emoticon.name}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: 'grey.100',
                                                fontSize: 24,
                                            }}
                                        >
                                            {e.emoticon.image ? null : (e.emoticon.emoji_text || '❔')}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={e.emoticon.name}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                        secondary={e.is_equipped ? '장착중' : null}
                                        secondaryTypographyProps={{ color: 'primary.main' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Grid container spacing={2} justifyContent="flex-start">
                            {slice.map(e => (
                                <Grid item key={e.id} sx={{ flex: '0 0 auto', width: CARD_W }}>
                                    <Card
                                        sx={{
                                            width: CARD_W,
                                            height: CARD_H,
                                            position: 'relative',
                                            border: e.is_equipped ? 2 : 1,
                                            borderColor: e.is_equipped ? 'primary.main' : 'grey.300',
                                            ...hoverFX,
                                        }}
                                    >
                                        {e.is_equipped && (
                                            <Chip
                                                label="장착중"
                                                variant="filled"
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    left: 6,
                                                    bgcolor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                    fontWeight: 400,
                                                    borderRadius: 1,
                                                    px: 1,
                                                    py: 0.2,
                                                }}
                                            />
                                        )}

                                        {e.emoticon.image ? (
                                            <CardMedia
                                                component="img"
                                                image={getFullImageUrl(e.emoticon.image)}
                                                alt={e.emoticon.name}
                                                height={100}
                                            />
                                        ) : (
                                            <Box
                                                height={100}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                fontSize={40}
                                            >
                                                {e.emoticon.emoji_text || '❔'}
                                            </Box>
                                        )}

                                        <CardContent
                                            sx={{
                                                pt: 1,
                                                px: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                noWrap
                                            >
                                                {e.emoticon.name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                color={e.is_equipped ? 'primary' : 'default'}
                                                onClick={() => handleToggle(e.emoticon.id)}
                                            >
                                                {e.is_equipped ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                            </IconButton>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}

            {!loading && (
                (!isMobile && pageCount > 1) ||
                (isMobile && shouldPaginateMobile)
            ) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={(_, v) => setPage(v)}
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}
        </Box>
    );
}
